import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CategoryFilters } from '@alkor/types';
import { api } from '@/lib/api';
import { formatNumber, pluralize } from '@alkor/utils';
import { haptic } from '@/lib/telegram';
import { IconCheck } from './icons';

export interface FilterDraft {
  available: boolean;
  brands: string[];
  priceMin: string;
  priceMax: string;
  /** ключ характеристики → «5..20» для range либо точное значение */
  specs: Record<string, string>;
}

export const EMPTY_FILTERS: FilterDraft = {
  available: false, brands: [], priceMin: '', priceMax: '', specs: {},
};

/** Сколько фильтров реально включено — для счётчика на чипе. */
export function countFilters(f: FilterDraft): number {
  return (
    (f.available ? 1 : 0) +
    f.brands.length +
    (f.priceMin || f.priceMax ? 1 : 0) +
    Object.keys(f.specs).length
  );
}

/** Параметры запроса из состояния фильтров. Один формат на витрину и на URL. */
export function toQuery(f: FilterDraft) {
  const spec = Object.entries(f.specs).map(([k, v]) => `${k}~${v}`);
  return {
    ...(f.available ? { available: true } : {}),
    ...(f.brands.length ? { brand: f.brands.join(',') } : {}),
    ...(f.priceMin ? { priceMin: Number(f.priceMin) } : {}),
    ...(f.priceMax ? { priceMax: Number(f.priceMax) } : {}),
    ...(spec.length ? { spec } : {}),
  };
}

export function filtersToSearchParams(f: FilterDraft, sort: string): URLSearchParams {
  const p = new URLSearchParams();
  if (sort !== 'popular') p.set('sort', sort);
  if (f.available) p.set('available', 'true');
  if (f.brands.length) p.set('brand', f.brands.join(','));
  if (f.priceMin) p.set('price_min', f.priceMin);
  if (f.priceMax) p.set('price_max', f.priceMax);
  for (const [k, v] of Object.entries(f.specs)) p.append('spec', `${k}~${v}`);
  return p;
}

export function filtersFromSearchParams(p: URLSearchParams): FilterDraft {
  const specs: Record<string, string> = {};
  for (const raw of p.getAll('spec')) {
    const at = raw.indexOf('~');
    if (at > 0) specs[raw.slice(0, at)] = raw.slice(at + 1);
  }
  return {
    available: p.get('available') === 'true',
    brands: p.get('brand')?.split(',').filter(Boolean) ?? [],
    priceMin: p.get('price_min') ?? '',
    priceMax: p.get('price_max') ?? '',
    specs,
  };
}

function parseRange(v: string | undefined): [string, string] {
  if (!v) return ['', ''];
  const parts = v.split('..');
  return [parts[0] ?? '', parts[1] ?? ''];
}

function buildRange(min: string, max: string): string | null {
  if (!min && !max) return null;
  return `${min}..${max}`;
}

export function FilterSheet({
  categorySlug, value, onApply, onClose,
}: {
  categorySlug: string;
  value: FilterDraft;
  onApply: (next: FilterDraft) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<FilterDraft>(value);

  const filtersQuery = useQuery({
    queryKey: ['filters', categorySlug],
    queryFn: () => api.filters(categorySlug),
    staleTime: 300_000,
  });

  // Живой счётчик: сколько товаров останется, если применить.
  // Считается тем же эндпоинтом, что и список — расхождений быть не может.
  const preview = useQuery({
    queryKey: ['filter-preview', categorySlug, draft],
    queryFn: () => api.products({ category: categorySlug, limit: 1, ...toQuery(draft) }),
    staleTime: 10_000,
  });

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onEsc);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const data: CategoryFilters | undefined = filtersQuery.data;
  const found = preview.data?.meta?.total ?? 0;
  const active = useMemo(() => countFilters(draft), [draft]);

  const toggleBrand = (slug: string) => {
    haptic.select();
    setDraft((d) => ({
      ...d,
      brands: d.brands.includes(slug) ? d.brands.filter((b) => b !== slug) : [...d.brands, slug],
    }));
  };

  const setSpec = (key: string, v: string | null) => {
    haptic.select();
    setDraft((d) => {
      const specs = { ...d.specs };
      if (v === null) delete specs[key];
      else specs[key] = v;
      return { ...d, specs };
    });
  };

  return (
    <div className="sheet-overlay" onClick={onClose} role="presentation">
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Фильтры"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet__grip" />
        <div className="sheet__head">
          <h3>Фильтры</h3>
          <button
            type="button"
            className="linkbtn"
            disabled={active === 0}
            onClick={() => setDraft(EMPTY_FILTERS)}
          >
            Сбросить
          </button>
        </div>

        <div className="sheet__body">
          <section className="sheet__group">
            <h4>Наличие</h4>
            <div className="chips">
              <button
                type="button"
                className={`fchip${draft.available ? ' fchip--on' : ''}`}
                onClick={() => setDraft((d) => ({ ...d, available: !d.available }))}
              >
                {draft.available && <IconCheck size={14} />}
                Только доступные к заказу
              </button>
            </div>
          </section>

          <section className="sheet__group">
            <h4>Цена, ₽</h4>
            <div className="range-row">
              <input
                inputMode="numeric"
                placeholder={data ? `от ${formatNumber(data.priceMin)}` : 'от'}
                value={draft.priceMin}
                onChange={(e) => setDraft((d) => ({ ...d, priceMin: e.target.value.replace(/\D/g, '') }))}
                aria-label="Цена от"
              />
              <span>—</span>
              <input
                inputMode="numeric"
                placeholder={data ? `до ${formatNumber(data.priceMax)}` : 'до'}
                value={draft.priceMax}
                onChange={(e) => setDraft((d) => ({ ...d, priceMax: e.target.value.replace(/\D/g, '') }))}
                aria-label="Цена до"
              />
            </div>
          </section>

          {(data?.brands.length ?? 0) > 0 && (
            <section className="sheet__group">
              <h4>Бренд</h4>
              <div className="chips">
                {data!.brands.map((b) => (
                  <button
                    key={b.slug}
                    type="button"
                    className={`fchip${draft.brands.includes(b.slug) ? ' fchip--on' : ''}`}
                    onClick={() => toggleBrand(b.slug)}
                  >
                    {b.name}
                    <span className="fchip__count">{b.count}</span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Набор характеристик задаётся в админке для каждой категории отдельно */}
          {(data?.specs ?? []).map((s) => {
            if (s.type === 'range') {
              const [min, max] = parseRange(draft.specs[s.key]);
              return (
                <section className="sheet__group" key={s.key}>
                  <h4>{s.label}{s.unit ? `, ${s.unit}` : ''}</h4>
                  <div className="range-row">
                    <input
                      inputMode="decimal"
                      placeholder={s.min !== undefined ? `от ${s.min}` : 'от'}
                      value={min}
                      onChange={(e) => setSpec(s.key, buildRange(e.target.value.replace(/[^\d.]/g, ''), max))}
                      aria-label={`${s.label} от`}
                    />
                    <span>—</span>
                    <input
                      inputMode="decimal"
                      placeholder={s.max !== undefined ? `до ${s.max}` : 'до'}
                      value={max}
                      onChange={(e) => setSpec(s.key, buildRange(min, e.target.value.replace(/[^\d.]/g, '')))}
                      aria-label={`${s.label} до`}
                    />
                  </div>
                </section>
              );
            }
            return (
              <section className="sheet__group" key={s.key}>
                <h4>{s.label}</h4>
                <div className="chips">
                  {(s.values ?? []).map((v) => (
                    <button
                      key={v}
                      type="button"
                      className={`fchip${draft.specs[s.key] === v ? ' fchip--on' : ''}`}
                      onClick={() => setSpec(s.key, draft.specs[s.key] === v ? null : v)}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </section>
            );
          })}

          {filtersQuery.isLoading && (
            <div className="sheet__group">
              <div className="skel" style={{ height: 14, width: '40%' }} />
              <div className="skel" style={{ height: 34, width: '100%', marginTop: 10 }} />
            </div>
          )}
        </div>

        <div className="sheet__foot">
          <button
            type="button"
            className="mainbtn"
            disabled={found === 0}
            onClick={() => { haptic.light(); onApply(draft); }}
          >
            {preview.isFetching
              ? 'Считаем…'
              : found === 0
                ? 'Ничего не найдено'
                : `Показать ${pluralize(found, 'товар', 'товара', 'товаров')}`}
          </button>
        </div>
      </div>
    </div>
  );
}
